/**
 * Orte suchen: drei Dienste, drei Aufgaben.
 *
 * Vorschläge beim Tippen kommen von Photon und, bei Hausadressen, von swisstopo.
 * Nominatim verbietet Autocomplete in seiner Nutzungsrichtlinie ausdrücklich -
 * es wird deshalb nur noch gefragt, wenn jemand die Suche absendet, und für die
 * eine Adresse beim Klick in die Karte.
 *
 * swisstopo nur mit `address` und `zipcode`, nicht mit `gazetteer`: dort stehen
 * die Lokalnamen in Mundart, und die Karte schreibt Schriftsprache.
 *
 * Alle Treffer haben die Form einer Nominatim-Antwort. Der Rest der Oberfläche
 * kennt nur diese eine.
 */
const PHOTON = 'https://photon.komoot.io/api/';
const NOMINATIM = 'https://nominatim.openstreetmap.org';
const SWISSTOPO = 'https://api3.geo.admin.ch/rest/services/api/SearchServer';

/** Gleiche Ausdehnung wie die Karte - was darüber hinausliegt, wäre unerreichbar. */
const AUSDEHNUNG = [3.57, 44.18, 13.66, 48.88];

/**
 * Was bei Photon sonst die vorderen Plätze belegt: "bahnhof zug" lieferte zuerst
 * Carsharing, einen Briefkasten und eine Ladestation.
 */
const NICHT_GEMEINT = [
  'amenity:car_sharing', 'amenity:post_box', 'amenity:charging_station',
  'amenity:parking_entrance', 'amenity:bench', 'amenity:waste_basket',
  'amenity:vending_machine', 'amenity:atm', 'information:guidepost',
  'information:board', 'information:map', 'man_made:survey_point',
  'highway:platform', 'railway:platform', 'railway:stop', 'railway:tram_stop',
  'highway:motorway_junction', 'highway:service'
];

/** Worum es meistens geht, wenn der Name passt. */
const GEWICHT = {
  'place:city': 40, 'place:town': 38, 'place:village': 34, 'place:municipality': 34,
  'natural:peak': 34, 'railway:station': 30, 'place:suburb': 22, 'place:quarter': 20,
  'place:neighbourhood': 18, 'place:hamlet': 18, 'place:state': 14,
  'tourism:attraction': 12, 'natural:water': 12, 'aerialway:station': 10
};

const OSM_TYP = { N: 'node', W: 'way', R: 'relation' };

/**
 * Lesbarer Name eines Treffers.
 *
 * Bei einer Hausadresse ist `name` leer und `display_name` beginnt mit der
 * Hausnummer: "Wilistrasse 7b" kam als `{name: "", display_name: "7b,
 * Bahnhofstrasse, ..."}` zurück - und `name || display_name.split(", ")[0]`
 * machte daraus das nackte "7b". Der Name wird deshalb aus den Adressfeldern
 * gebaut, in Schweizer Reihenfolge: Strasse, dann Nummer.
 */
export function ortsname(hit, { mitOrt = false } = {}) {
  const a = hit.address || {};
  const strasse = a.road || a.pedestrian || a.footway || a.path || '';
  const nummer = a.house_number || '';
  // Strassen und Hausadressen gibt es dutzendfach gleich benannt - sie
  // brauchen den Ort dazu. Ein Berg oder eine Stadt trägt seinen Namen schon
  // selbst; "Pilatus, Alpnach" wäre nur länger.
  const istAdresse = !hit.name || ['road', 'house'].includes(hit.addresstype);
  const name0 = hit.name
    || (strasse ? (nummer ? `${strasse} ${nummer}` : strasse) : '')
    || String(hit.display_name || '').split(', ')[0]
    || '';
  let name = name0;
  if (mitOrt && istAdresse && name) {
    const ort = a.city || a.town || a.village || a.hamlet || a.municipality || '';
    if (ort && !name.includes(ort)) name += `, ${ort}`;
  }
  return name;
}

/** Was von `display_name` übrig bleibt, wenn der Name schon dasteht. */
export function ortsdetail(hit, kopf) {
  const teile = String(hit.display_name || '').split(', ');
  while (teile.length && kopf.includes(teile[0])) teile.shift();
  return teile.join(', ');
}

const norm = (s) => String(s || '').toLowerCase().normalize('NFD')
  .replace(/[̀-ͯ]/g, '').replace(/\s+/g, ' ').trim();

/** Photon-Merkmal -> Treffer in Nominatim-Form. */
export function ausPhoton(f) {
  const p = f.properties || {};
  const [lng, lat] = f.geometry?.coordinates || [];
  const strasse = p.street || '';
  const ort = p.city || p.town || p.village || p.district || '';
  // Reihenfolge wie auf einem Briefumschlag: was schon im Namen steht, nicht nochmal.
  const zeile = [
    p.name,
    p.name !== strasse && strasse ? [strasse, p.housenumber].filter(Boolean).join(' ') : null,
    [p.postcode, ort].filter(Boolean).join(' ') || null,
    p.state
  ].filter(Boolean);
  const [w, n, o, s] = Array.isArray(p.extent) ? p.extent : [];
  return {
    lat,
    lon: lng,
    name: p.name || '',
    display_name: zeile.join(', ') || [strasse, p.housenumber, ort].filter(Boolean).join(', '),
    address: {
      road: strasse,
      house_number: p.housenumber || '',
      city: ort,
      postcode: p.postcode || ''
    },
    osm_type: OSM_TYP[p.osm_type],
    osm_id: p.osm_id,
    addresstype: p.housenumber && !p.name ? 'house' : (p.type === 'street' ? 'road' : p.osm_value),
    type: p.osm_value,
    schluessel: `${p.osm_key}:${p.osm_value}`,
    boundingbox: Number.isFinite(w) && Number.isFinite(s) ? [s, n, w, o] : undefined
  };
}

/** swisstopo-Treffer -> dieselbe Form. Das Etikett trägt Auszeichnungen. */
export function ausSwisstopo(r) {
  const a = r.attrs || {};
  const roh = String(a.label || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  const istPlz = a.origin === 'zipcode';
  if (istPlz) {
    // "8400 - Winterthur"
    const [, plz = '', ort = roh] = /^(\d{4})\s*-\s*(.+)$/.exec(roh) || [];
    return {
      lat: a.lat,
      lon: a.lon,
      name: [plz, ort].filter(Boolean).join(' '),
      display_name: 'Postleitzahl',
      address: { city: ort, postcode: plz },
      addresstype: 'postcode',
      type: 'postcode',
      schluessel: 'place:postcode',
      quelle: 'swisstopo'
    };
  }
  // "Bahnhofstrasse 10 8400 Winterthur" - die Postleitzahl trennt die Teile.
  const m = /^(.*?)\s+(\d{4})\s+(.+)$/.exec(roh);
  const strasse = m ? m[1] : roh;
  const ort = m ? m[3].replace(/\s+[A-Z]{2}$/, '') : '';
  return {
    lat: a.lat,
    lon: a.lon,
    name: strasse,
    display_name: m ? `${strasse}, ${m[2]} ${m[3]}` : roh,
    address: { road: strasse, house_number: '', city: ort, postcode: m ? m[2] : '' },
    addresstype: 'house',
    type: 'house',
    schluessel: 'place:house',
    quelle: 'swisstopo'
  };
}

/**
 * swisstopo sucht unscharf: auf "Winterthur Bahnhofstrasse 10" kommt zuerst die
 * Färberstrasse 10, weil Ort und Nummer passen. Eine Adresse zählt nur, wenn ihre
 * Strasse auch in der Frage vorkommt.
 */
export function adressePasst(t, q) {
  if (t.addresstype !== 'house') return true;
  // Jedes Wort der Strasse zählt: "landstrasse 5" meint auch die Alte Landstrasse.
  const strasse = norm(t.name).split(' ').filter((w) => /\D/.test(w) && w.length > 2);
  const frage = norm(q).split(' ').filter((w) => w.length >= 3);
  // Auch abgekürzt ("bahnhofstr 10") und mit kleinem Tippfehler am Ende.
  return strasse.some((wort) => frage.some((w) =>
    wort.startsWith(w) || w.startsWith(wort.slice(0, Math.max(4, wort.length - 2)))));
}

/**
 * Photon ordnet nach Textähnlichkeit: bei "pilatus" steht ein Industriegebiet
 * vor dem Hotel und beide vor der Bergstation. Hier zählt, was man vermutlich
 * meint. Die Reihenfolge von Photon bleibt als schwaches Merkmal erhalten.
 */
export function ordnen(treffer, q) {
  const frage = norm(q);
  const bewertet = treffer.map((t, i) => {
    const name = norm(t.name);
    let punkte = -i;
    if (name === frage) punkte += 50;
    else if (name.startsWith(frage)) punkte += 15;
    punkte += GEWICHT[t.schluessel] || 0;
    if (t.quelle === 'swisstopo') punkte += /\d/.test(q) ? 60 : 0;
    return { t, punkte };
  });
  bewertet.sort((a, b) => b.punkte - a.punkte);

  // Derselbe Ort mehrfach: Bahnhof mit Gleisen, Strasse mit Abschnitten.
  const gesehen = [];
  const raus = [];
  for (const { t } of bewertet) {
    const doppelt = gesehen.some((g) =>
      norm(g.name) === norm(t.name) && norm(g.address?.city) === norm(t.address?.city)
      && t.name && Math.abs(g.lat - t.lat) < 0.01 && Math.abs(g.lon - t.lon) < 0.01
      && g.schluessel === t.schluessel);
    if (doppelt) continue;
    gesehen.push(t);
    raus.push(t);
  }
  return raus;
}

async function photon(q, { lat, lng, signal, limit }) {
  const p = new URLSearchParams({
    q, limit: String(limit), lang: 'de',
    bbox: AUSDEHNUNG.join(','),
    location_bias_scale: '0.3'
  });
  if (Number.isFinite(lat)) { p.set('lat', lat); p.set('lon', lng); }
  for (const tag of NICHT_GEMEINT) p.append('osm_tag', `!${tag}`);
  const r = await fetch(`${PHOTON}?${p}`, { signal });
  if (!r.ok) throw new Error(`Photon ${r.status}`);
  return ((await r.json()).features || []).map(ausPhoton);
}

async function swisstopo(q, { signal }) {
  const p = new URLSearchParams({
    searchText: q, type: 'locations', origins: 'address,zipcode', limit: '5', sr: '4326'
  });
  const r = await fetch(`${SWISSTOPO}?${p}`, { signal });
  if (!r.ok) throw new Error(`swisstopo ${r.status}`);
  return ((await r.json()).results || []).map(ausSwisstopo);
}

/**
 * Vorschläge beim Tippen. Fällt ein Dienst aus, reicht der andere; nur wenn
 * beide ausfallen, gibt es einen Fehler und die Oberfläche kann es sagen.
 */
export async function vorschlaege(q, { map, signal, limit = 8 } = {}) {
  const mitte = map?.getCenter();
  const antworten = await Promise.allSettled([
    photon(q, { lat: mitte?.lat, lng: mitte?.lng, signal, limit: 14 }),
    // Ohne Ziffer ist es selten eine Adresse - dann die Abfrage sparen.
    /\d/.test(q)
      ? swisstopo(q, { signal }).then((liste) => liste.filter((t) => adressePasst(t, q)))
      : Promise.resolve([])
  ]);
  if (signal?.aborted) throw new DOMException('abgebrochen', 'AbortError');
  if (antworten.every((a) => a.status === 'rejected')) throw new Error('Suche nicht erreichbar.');
  const alle = antworten.flatMap((a) => (a.status === 'fulfilled' ? a.value : []))
    .filter((t) => Number.isFinite(Number(t.lat)) && Number.isFinite(Number(t.lon)));
  return ordnen(alle, q).slice(0, limit);
}

/** Abgesendete Suche: Nominatim, das ist erlaubt und trifft oft genauer. */
export async function nominatim(q, { map, signal, limit = 8 } = {}) {
  const b = map?.getBounds();
  const p = new URLSearchParams({
    q,
    format: 'jsonv2',
    limit: String(limit),
    addressdetails: '1',
    'accept-language': 'de-CH',
    viewbox: b ? [b.getWest(), b.getNorth(), b.getEast(), b.getSouth()].join(',') : '',
    bounded: '0',
    countrycodes: 'ch,li,de,fr,it,at'
  });
  const r = await fetch(`${NOMINATIM}/search?${p}`, { signal, headers: { Accept: 'application/json' } });
  if (!r.ok) throw new Error('Ortssuche nicht erreichbar.');
  const [w, s, o, n] = AUSDEHNUNG;
  return (await r.json()).filter((t) => {
    const lat = Number(t.lat), lng = Number(t.lon);
    return lng >= w && lng <= o && lat >= s && lat <= n;
  });
}

/** Ein einzelner Treffer zu einem freien Text - für ein abgesendetes Routenfeld. */
export async function geocode(query, map) {
  // Mehr als einen anfordern: `nominatim` wirft weg, was ausserhalb der Karte liegt,
  // und wäre das der einzige gewesen, hiesse es fälschlich „nicht gefunden“.
  const [hit] = await nominatim(query, { map, limit: 5 });
  if (!hit) throw new Error(`"${query}" nicht gefunden.`);
  return { lat: Number(hit.lat), lng: Number(hit.lon), name: ortsname(hit, { mitOrt: true }) };
}

/** Die Adresse an einer Stelle - für den Klick auf etwas ohne Namen. */
export async function rueckwaerts({ lat, lng }, { signal } = {}) {
  const p = new URLSearchParams({
    lat, lon: lng, format: 'jsonv2', addressdetails: '1', zoom: '18', 'accept-language': 'de-CH'
  });
  const r = await fetch(`${NOMINATIM}/reverse?${p}`, { signal, headers: { Accept: 'application/json' } });
  if (!r.ok) return null;
  const d = await r.json();
  if (!d || d.error) return null;
  const a = d.address || {};
  const strasse = a.road || a.pedestrian || a.footway || a.path || '';
  const ort = a.city || a.town || a.village || a.hamlet || a.municipality || '';
  const zeile1 = [strasse, a.house_number].filter(Boolean).join(' ');
  const zeile2 = [a.postcode, ort].filter(Boolean).join(' ');
  return {
    kurz: zeile1 || ort || d.name || '',
    voll: [zeile1, zeile2].filter(Boolean).join(', ')
  };
}

/** Höhe aus swisstopos Modell. Ausserhalb der Schweiz antwortet es mit einem Fehler. */
export async function hoeheBei(ost, nord, { signal } = {}) {
  const p = new URLSearchParams({ easting: ost.toFixed(1), northing: nord.toFixed(1), sr: '2056' });
  const r = await fetch(`https://api3.geo.admin.ch/rest/services/height?${p}`, { signal });
  if (!r.ok) return null;
  const h = Number((await r.json()).height);
  return Number.isFinite(h) && h > 0 ? Math.round(h) : null;
}
